import { IconButton, SnackbarContent, makeStyles } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import InfoIcon from '@material-ui/icons/Info'
import CloseIcon from '@material-ui/icons/Close'
import WarningIcon from '@material-ui/icons/Warning'
import amber from '@material-ui/core/colors/amber'
import classNames from 'classnames'
import { pascalCase } from 'pascal-case'
import PropTypes from 'prop-types'
import React from 'react'

import { Typography } from 'src/modules/ui'

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
}

const useStyles = makeStyles(theme => ({
  root: {
    flexWrap: 'none',
  },
  success: {
    backgroundColor: theme.palette.lightGreen.main,
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  info: {
    backgroundColor: theme.palette.grey[300],
  },
  warning: {
    backgroundColor: amber[700],
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  imageContainer: {
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    overflow: 'hidden',
    height: 125,
    width: 125,
  },
  image: {
    width: 125,
  },
  imageTomato: {
    width: 122,
  },
  imageAubergines: {
    width: 111,
  },
  imageCarrots: {
    width: 86,
  },
  imageLeeks: {
    width: 65,
  },
  imageMushrooms: {
    marginBottom: 10,
    width: 100,
  },
  imageOnion: {
    marginRight: 10,
    width: 89,
  },
  imageOrange: {
    width: 123,
    marginBottom: 5,
    marginRight: 5,
  },
  imagePeas: {
    width: 60,
    marginBottom: 2,
    marginRight: 15,
  },
  imagePotato: {
    width: 110,
  },
  imageThyme: {
    width: 75,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  messageText: {
    lineHeight: 1,
    whiteSpace: 'pre-line',
  },
}))

const NotificationContent = React.forwardRef(
  ({ className, image, message, onClose, variant, ...props }, ref) => {
    const classes = useStyles()
    const Icon = variantIcon[variant]

    return (
      <SnackbarContent
        className={classNames(classes.root, classes[variant], className)}
        message={
          <span id="client-snackbar" className={classes.message}>
            {!image ? (
              <Icon className={classNames(classes.icon, classes.iconVariant)} />
            ) : (
              <div className={classes.imageContainer}>
                <img
                  className={classNames(
                    classes.image,
                    classes[`image${pascalCase(image)}`]
                  )}
                  src={`/images/${image}.png`}
                  alt="message"
                />
              </div>
            )}
            <Typography className={classes.messageText}>{message}</Typography>
          </span>
        }
        action={[
          <IconButton
            key="close"
            color="inherit"
            className={classes.close}
            onClick={onClose}
          >
            <CloseIcon className={classes.icon} />
          </IconButton>,
        ]}
        ref={ref}
        {...props}
      />
    )
  }
)

NotificationContent.propTypes = {
  className: PropTypes.string,
  message: PropTypes.node,
  image: PropTypes.oneOf([
    undefined,
    'aubergines',
    'carrots',
    'leeks',
    'mushrooms',
    'onion',
    'orange',
    'peas',
    'potato',
    'thyme',
    'tomato',
  ]),
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['success', 'warning', 'error', 'info']).isRequired,
}

export default NotificationContent
